import BaseApiClient from "@/api/base-client";
import { SortOption } from "@/types/external-content";
class ExternalContentApi extends BaseApiClient {
    BASE_PATH = "/api/studies";
    contentPath(studyId, activityId, contentId) {
        const base = `${this.BASE_PATH}/${studyId}/activities/${activityId}/external-content`;
        return contentId ? `${base}/${contentId}` : base;
    }
    getConfig = async (studyId, activityId) => {
        return this.get(`${this.contentPath(studyId, activityId)}/config`);
    };
    list = async (studyId, activityId, sort = SortOption.RECENT) => {
        const response = await this.get(this.contentPath(studyId, activityId), {
            params: { sort }
        });
        return response.contents;
    };
    create = async (studyId, activityId, data) => {
        return this.post(this.contentPath(studyId, activityId), { data });
    };
    remove = async (studyId, activityId, contentId) => {
        return this.delete(this.contentPath(studyId, activityId, contentId));
    };
    getById = async (studyId, activityId, contentId) => {
        return this.get(this.contentPath(studyId, activityId, contentId));
    };
    updateMetadata = async (studyId, activityId, contentId, data) => {
        return this.put(`${this.contentPath(studyId, activityId, contentId)}/metadata`, {
            data
        });
    };
    /**
     * Per-engine pipeline state for the content. One row per *attempted* engine;
     * engines absent from the array have never been transcribed.
     */
    getEngineStates = async (studyId, activityId, contentId) => {
        return this.get(`${this.contentPath(studyId, activityId, contentId)}/transcripts`);
    };
    /**
     * Fetch transcript content. Omitting `engine` resolves the primary engine's
     * transcript via `/transcripts/primary` (the only path non-Super-Admin
     * viewers may request). Super-Admin viewers pass an explicit engine ID.
     */
    getTranscript = async (studyId, activityId, contentId, engine, exportMode) => {
        const path = engine != null
            ? `${this.contentPath(studyId, activityId, contentId)}/transcripts/${engine}`
            : `${this.contentPath(studyId, activityId, contentId)}/transcripts/primary`;
        return this.get(path, {
            params: exportMode ? { export: true } : undefined
        });
    };
    /**
     * Initiate or re-initiate transcription for a specific engine. The engine
     * is conveyed in the URL path; the request carries no body.
     */
    transcribe = async (studyId, activityId, contentId, engine) => {
        return this.post(`${this.contentPath(studyId, activityId, contentId)}/transcripts/${engine}`);
    };
    setPrimaryEngine = async (studyId, activityId, contentId, engine) => {
        return this.put(`${this.contentPath(studyId, activityId, contentId)}/transcripts/primary`, {
            data: { engine }
        });
    };
    fetchSummary = async (studyId, activityId, contentId, exportMode) => {
        return this.get(`${this.contentPath(studyId, activityId, contentId)}/summaries`, {
            params: exportMode ? { export: true } : undefined
        });
    };
    regenerateSummary = async (studyId, activityId, contentId, contentType) => {
        return this.post(`${this.contentPath(studyId, activityId, contentId)}/summaries/${contentType}`);
    };
    acknowledgeLiveMinutes = async (studyId, activityId) => {
        return this.post(`${this.contentPath(studyId, activityId)}/live-minute-acknowledgment`);
    };
    /**
     * Sync activity start/end dates to encompass all content source dates.
     */
    syncDates = async (studyId, activityId) => {
        return this.put(`${this.contentPath(studyId, activityId)}/sync-dates`);
    };
    static instance = new ExternalContentApi();
}
export default ExternalContentApi.instance;
